import React, { FC } from 'react';
import { get, compact, find } from 'lodash';
import { Tooltip } from '@src/components/Tooltip';
import { Link } from '@src/components/Link';
import { Label } from '@src/components/Label';
import { OpeningActions } from '@src/components/OpeningActions';
import { ActionMenuPopover } from '@src/components/Popover';
import { FUNNEL_EDIT_ROUTE, FUNNEL_ROUTE } from '@src/Routes/links';
import { EMPLOYMENT_TYPES, OPENING_PAID_ADDONS_BY_TYPE } from '@src/constants';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { getStatus, printStatus, isActive, isDraft, isAdminReview } from '@src/util/opening';
import { createLink } from '@src/util/createLink';
import { trackEvent, trackViewCreateTargeting } from '@src/metrics';
import { ActiveView } from '@src/views/Opening/enums/activeView.enum';
import { OpeningsQuery } from '@src/graphql/generated';
import { HeaderProps } from './props';
import './styles.scss';

export const Header: FC<HeaderProps> = ({ opening, onUpdateOpening }) => {
  const state = getStatus(opening.state);

  const openingId = opening.id;

  const isOpeningDraft = isDraft(opening.state);

  const labelType = (isOpeningDraft || isActive(opening.state) || isAdminReview(opening.state)) && 'invisible';

  const getSubtitle = (opening: OpeningsQuery['openings']['result'][0]): string => {
    const type = find(EMPLOYMENT_TYPES, ({ key }) => key === opening.employmentType);

    return compact([get(type, 'name'), get(opening, 'speciality.name')]).join(' • ');
  };

  return (
    <div className="opening-group">
      <Link
        className="opening-group__link"
        onClick={() =>
          isOpeningDraft
            ? trackViewCreateTargeting({
                'Event Source': MetricEventSource.OPENING_LIST_ITEM,
                'Previous Context': MetricContext.OPENINGS_LIST,
              })
            : trackEvent({
                context: MetricContext.CANDIDATE_MANAGEMENT,
                interaction: MetricAction.OPEN_OPENING_VIEW,
                payload: {
                  'Event Source': MetricEventSource.OPENING_LIST_ITEM,
                  'Opening Id': openingId,
                  'Previous Context': MetricContext.OPENINGS_LIST,
                },
              })
        }
        to={
          isOpeningDraft
            ? createLink(FUNNEL_EDIT_ROUTE, { params: { openingId } })
            : createLink(FUNNEL_ROUTE, {
                params: { openingId },
                queryParams: {
                  activeView: ActiveView.INVITE_CANDIDATES,
                },
              })
        }
      >
        <div className="opening-group__text">
          <h4>{opening.title || 'Title missing'}</h4>
          <p>{getSubtitle(opening)}</p>
        </div>

        <div className="opening-group__labels">
          {opening.paidAddons.map(({ type }, index) => (
            <Label
              key={index}
              text={OPENING_PAID_ADDONS_BY_TYPE[type]?.label}
              type={labelType}
              wrapperClassName={'opening-group__label'}
            />
          ))}

          <Tooltip
            overlayClassName="opening-group__tooltip"
            placement="top"
            tooltip={<span>{printStatus(opening.state)}</span>}
          >
            <Label
              indicator
              indicatorClassName={`opening-group-state__indicator-${state}`}
              text={state}
              type={labelType}
              wrapperClassName={`opening-group-state__${state}`}
            />
          </Tooltip>
        </div>
      </Link>

      <div className="opening-group__actions-menu">
        <OpeningActions
          metricsPrevContext={MetricContext.OPENINGS_LIST}
          opening={opening}
          updateOpening={onUpdateOpening}
        >
          {({ actions, handleAction }) => <ActionMenuPopover actions={actions} onAction={handleAction} />}
        </OpeningActions>
      </div>
    </div>
  );
};
